import { useMutation } from '@apollo/client';

import * as Types from '@xing-com/crate-common-graphql-types';
import {
  DreamJobsInteractDocument,
  type DreamJobsInteractMutation,
  type DreamJobsInteractMutationVariables,
  DreamJobsUpdateInteractDocument,
  type DreamJobsUpdateInteractMutation,
  type DreamJobsUpdateInteractMutationVariables,
} from '@xing-com/crate-dream-machine-graphql';

import {
  useJobCardTracking,
  getTrackingParams,
} from '../../cards/job-card/use-job-card-tracking';
import { useSearchAndShuffleContext } from '../../context';
import { TakenCard, AvailableCard } from '../../types';
import { useConsent } from '../../use-consent';

import { useVomp } from './use-vomp';

type useHandleInteractionReturn = {
  handleInteraction: (
    card: TakenCard | AvailableCard,
    deckIndex: number,
    interactionType: Types.DreamJobInteractionType,
    isUpdating?: boolean
  ) => Promise<boolean>;
};

export const useHandleInteraction = (): useHandleInteractionReturn => {
  const { consentTaken } = useSearchAndShuffleContext();
  const { addConsent } = useConsent();
  const { trackDislike, trackLike, trackSuperLike } = useJobCardTracking();
  const { callVomp } = useVomp();
  const [mutateJobsInteract] = useMutation<
    DreamJobsInteractMutation,
    DreamJobsInteractMutationVariables
  >(DreamJobsInteractDocument);

  const [mutateJobsUpdateInteract] = useMutation<
    DreamJobsUpdateInteractMutation,
    DreamJobsUpdateInteractMutationVariables
  >(DreamJobsUpdateInteractDocument);

  const interaction = async (
    card: TakenCard | AvailableCard,
    interactionType: Types.DreamJobInteractionType,
    isUpdating = false
  ): Promise<boolean> => {
    const config = {
      variables: {
        jobId: card.data.id,
        jobType:
          card.type === 'AvailableJob'
            ? Types.DreamJobType.Available
            : Types.DreamJobType.Taken,
        interactionType,
      },
    };

    if (
      card.data.type === 'TakenJob' &&
      card.data.similarMembers?.length === 1 &&
      card.data.similarMembers?.[0]?.user?.id
    ) {
      callVomp(
        card.data.similarMembers?.[0]?.user?.id,
        card.data.similiarMembersVompWhitelist
      );
    }

    if (card.data.type === 'TakenJob' && !consentTaken) {
      addConsent();
    }

    if (isUpdating) {
      const { data } = await mutateJobsUpdateInteract(config);
      return (
        data?.dreamJobUpdateInteraction?.__typename === 'DreamJobsStoreSuccess'
      );
    } else {
      const { data } = await mutateJobsInteract(config);
      return (
        data?.dreamJobStoreInteraction?.__typename === 'DreamJobsStoreSuccess'
      );
    }
  };

  const handleInteraction = async (
    card: TakenCard | AvailableCard,
    deckIndex: number,
    interactionType: Types.DreamJobInteractionType,
    isUpdating = false
  ): Promise<boolean> => {
    const interactionSuccess = await interaction(
      card,
      interactionType,
      isUpdating
    );
    if (interactionSuccess) {
      const trackInteraction = {
        [Types.DreamJobInteractionType.Dislike]: trackDislike,
        [Types.DreamJobInteractionType.Like]: trackLike,
        [Types.DreamJobInteractionType.Super]: trackSuperLike,
      }[interactionType];

      if (trackInteraction) {
        trackInteraction(getTrackingParams(card, deckIndex));
      }
    }
    return interactionSuccess;
  };

  return {
    handleInteraction,
  };
};
